import { useLayoutEffect, useRef, useState } from "react";
import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import { formatActivationCountForDisplay } from "@/lib/format";

const VARIANTS = {
  ring: { numberPx: 48, wordRatio: 0.58, minScale: 0.52, wordGapEm: 0.2 },
  "metric-box": { numberPx: 22, wordRatio: 0.62, minScale: 0.65, wordGapEm: 0.18 },
} as const;

type Variant = keyof typeof VARIANTS;

type Props = {
  count: number;
  variant: Variant;
  className?: string;
  displayMode?: "integer" | "averaged";
  averageMode?: boolean;
};

export function ActivationCountText({
  count,
  variant,
  className,
  displayMode = "integer",
  averageMode = false,
}: Props) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode).format;
  const { numberPx, wordRatio, minScale, wordGapEm } = VARIANTS[variant];
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const displayCount = formatActivationCountForDisplay(count, displayMode);
  const roundedCount = Math.round(count);
  const word = roundedCount === 1 ? t.activationSingular : t.activationPlural;
  const isRing = variant === "ring";
  const primaryClass = averageMode
    ? "today-data-value-primary"
    : variant === "metric-box"
      ? "text-[oklch(90%_0.018_150)]"
      : "";

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const fit = () => {
      const available = container.clientWidth;
      if (available <= 0) return;

      const previousSize = content.style.fontSize;
      content.style.fontSize = `${numberPx}px`;
      const naturalWidth = content.scrollWidth;
      content.style.fontSize = previousSize;

      const fitScale = (available - 2) / naturalWidth;
      const nextScale = naturalWidth > available - 2 ? Math.max(minScale, fitScale) : 1;
      // Prefer readability over the scale floor when the container is still too narrow.
      const resolvedScale =
        naturalWidth * nextScale > available - 2 ? Math.max(0.45, fitScale) : nextScale;
      setScale(resolvedScale);
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [count, minScale, numberPx, word]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: isRing ? "center" : "flex-start",
      }}
    >
      <div
        ref={contentRef}
        className={`mono inline-flex items-baseline whitespace-nowrap font-semibold tracking-normal${
          primaryClass ? ` ${primaryClass}` : ""
        }`}
        style={{
          fontSize: numberPx * scale,
          lineHeight: 1.1,
          gap: `${wordGapEm}em`,
        }}
      >
        <span>{displayCount}</span>
        <span style={{ fontSize: `${wordRatio}em` }} className={primaryClass || undefined}>
          {word}
        </span>
      </div>
    </div>
  );
}
